import React, { useState, useEffect } from 'react';
import exercisesData from '../../data/exercises.json';
import PlaceholderImage from '../PlaceholderImage';

const ProgressionSuggestions = ({ suggestions, onSelect, onClose }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imageIndex, setImageIndex] = useState({});
  const { exercise, slotIndex, progressions } = suggestions;

  // Get full exercise data for suggestions
  const getExerciseData = (exerciseId) => {
    return exercisesData.find(ex => ex.id === exerciseId);
  };

  // Handle looping image animation on hover
  useEffect(() => {
    if (hoveredCard) {
      const interval = setInterval(() => {
        setImageIndex(prev => ({
          ...prev,
          [hoveredCard]: ((prev[hoveredCard] || 0) + 1) % 2
        }));
      }, 1000); // Change image every 1000ms (1 second)

      return () => clearInterval(interval);
    }
  }, [hoveredCard]);

  const renderSuggestionCard = (suggestion, type) => {
    const exerciseData = getExerciseData(suggestion.id);
    if (!exerciseData) return null;

    const typeColors = {
      easier: 'border-green-200 hover:border-green-300',
      harder: 'border-red-200 hover:border-red-300',
      variations: 'border-blue-200 hover:border-blue-300'
    };

    const typeLabels = {
      easier: 'Easier Alternative',
      harder: 'Harder Progression',
      variations: 'Variation'
    };

    return (
      <div
        key={suggestion.id}
        className={`border-2 ${typeColors[type]} rounded-lg p-3 cursor-pointer transition-all duration-200 hover:shadow-md`}
        onClick={() => onSelect(exerciseData)}
        onMouseEnter={() => setHoveredCard(suggestion.id)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        {/* Exercise Image */}
        <div className="w-full h-24 bg-gray-200 rounded-lg overflow-hidden mb-2">
          {exerciseData.images && exerciseData.images.length > 0 ? (
            exerciseData.images[0] === "placeholder.jpg" ? (
              <PlaceholderImage 
                className="w-full h-full"
              />
            ) : (
              <img
                src={`/exercise-images/${hoveredCard === suggestion.id && exerciseData.images.length > 1 ? exerciseData.images[imageIndex[suggestion.id] || 0] : exerciseData.images[0]}`}
                alt={exerciseData.name}
                className="w-full h-full object-cover transition-opacity duration-300"
              />
            )
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400 text-lg">📷</span>
            </div>
          )}
        </div>

        {/* Exercise Info */}
        <div className="space-y-1">
          <h4 className="font-medium text-sm text-gray-900 leading-tight">
            {exerciseData.name}
          </h4>
          <p className="text-xs text-gray-500">
            {exerciseData.primaryMuscles && exerciseData.primaryMuscles.length > 0
              ? exerciseData.primaryMuscles[0]
              : 'Multiple muscles'
            }
          </p>
          <p className="text-xs text-gray-500">
            {exerciseData.equipment || 'Body only'}
          </p>
        </div>

        {/* Type Badge */}
        <div className="mt-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            type === 'easier' ? 'bg-green-100 text-green-800' :
            type === 'harder' ? 'bg-red-100 text-red-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {typeLabels[type]}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Progression Suggestions for {exercise.name}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-6">
        {/* Easier Alternatives */}
        {progressions.easierAlternatives && progressions.easierAlternatives.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Easier Alternatives
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {progressions.easierAlternatives.map(suggestion => 
                renderSuggestionCard(suggestion, 'easier')
              )}
            </div>
          </div>
        )}

        {/* Harder Progressions */}
        {progressions.harderProgressions && progressions.harderProgressions.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
              Harder Progressions
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {progressions.harderProgressions.map(suggestion => 
                renderSuggestionCard(suggestion, 'harder')
              )}
            </div>
          </div>
        )}

        {/* Variations */}
        {progressions.variations && progressions.variations.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              Variations
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {progressions.variations.map(suggestion => 
                renderSuggestionCard(suggestion, 'variations')
              )}
            </div>
          </div>
        )}

        {/* No suggestions message */}
        {(!progressions.easierAlternatives || progressions.easierAlternatives.length === 0) &&
         (!progressions.harderProgressions || progressions.harderProgressions.length === 0) &&
         (!progressions.variations || progressions.variations.length === 0) && (
          <div className="text-center py-8">
            <p className="text-gray-500">No progression suggestions available for this exercise.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressionSuggestions;
